import { Divider, Flex, HStack, Stack } from '@chakra-ui/react';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanButton } from '../helivan/HelivanButton';
import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import {
  useDeleteExplorableMutation,
  useDeletePersonaMutation,
  useCreateOrganizationMutation,
  useAddUserToOrganizationMutation,
  useAddPersonaToCollectionMutation,
} from '../../generated/graphql';

export const InternalTooling = () => {
  const toast = useToast();

  // Modal disclosures
  const {
    isOpen: isDeleteExplorableOpen,
    onOpen: onOpenDeleteExplorable,
    onClose: onCloseDeleteExplorable,
  } = useDisclosure();

  const {
    isOpen: isDeletePersonaOpen,
    onOpen: onOpenDeletePersona,
    onClose: onCloseDeletePersona,
  } = useDisclosure();

  const {
    isOpen: isCreateOrganizationOpen,
    onOpen: onOpenCreateOrganization,
    onClose: onCloseCreateOrganization,
  } = useDisclosure();

  const {
    isOpen: isAddUserToOrgOpen,
    onOpen: onOpenAddUserToOrg,
    onClose: onCloseAddUserToOrg,
  } = useDisclosure();

  // Final confirmation modal disclosures
  const {
    isOpen: isExplorableFinalConfirmOpen,
    onOpen: onOpenExplorableFinalConfirm,
    onClose: onCloseExplorableFinalConfirm,
  } = useDisclosure();

  const {
    isOpen: isPersonaFinalConfirmOpen,
    onOpen: onOpenPersonaFinalConfirm,
    onClose: onClosePersonaFinalConfirm,
  } = useDisclosure();
  const {
    isOpen: isAddPersonaToCollectionOpen,
    onOpen: onOpenAddPersonaToCollection,
    onClose: onCloseAddPersonaToCollection,
  } = useDisclosure();

  // Form states
  const [explorableId, setExplorableId] = useState('');
  const [personaId, setPersonaId] = useState('');
  const [collectionId, setCollectionId] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [userId, setUserId] = useState('');
  const [organizationId, setOrganizationId] = useState('');

  // Mutations
  const [deleteExplorable, { loading: deleteExplorableLoading }] =
    useDeleteExplorableMutation({
      onCompleted: () => {
        toast({
          title: 'Success',
          description: 'Explorable deleted successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onCloseExplorableFinalConfirm();
        onCloseDeleteExplorable();
        setExplorableId('');
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    });

  const [deletePersona, { loading: deletePersonaLoading }] =
    useDeletePersonaMutation({
      onCompleted: () => {
        toast({
          title: 'Success',
          description: 'Persona deleted successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onClosePersonaFinalConfirm();
        onCloseDeletePersona();
        setPersonaId('');
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    });

  const [addPersonaToCollection, { loading: addPersonaToCollectionLoading }] =
    useAddPersonaToCollectionMutation({
      onCompleted: () => {
        toast({
          title: 'Success',
          description: 'Persona added to collection successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onCloseAddPersonaToCollection();
        setPersonaId('');
        setCollectionId('');
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    });

  const [createOrganization, { loading: createOrgLoading }] =
    useCreateOrganizationMutation({
      onCompleted: (result) => {
        toast({
          title: 'Success',
          description: `Organization created successfully: id is ${result.createOrganization.id}`,
          status: 'success',
          duration: 10000,
          isClosable: true,
        });
        onCloseCreateOrganization();
        setOrganizationName('');
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    });

  const [addUserToOrganization, { loading: addUserLoading }] =
    useAddUserToOrganizationMutation({
      onCompleted: () => {
        toast({
          title: 'Success',
          description: 'User added to organization successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onCloseAddUserToOrg();
        setUserId('');
        setOrganizationId('');
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    });

  // Handlers
  const handleInitiateDeleteExplorable = () => {
    onOpenExplorableFinalConfirm();
  };

  const handleDeleteExplorable = () => {
    if (explorableId) {
      deleteExplorable({ variables: { id: explorableId } });
    }
  };

  const handleInitiateDeletePersona = () => {
    onOpenPersonaFinalConfirm();
  };

  const handleDeletePersona = () => {
    if (personaId) {
      deletePersona({ variables: { id: personaId } });
    }
  };

  const handleCreateOrganization = () => {
    if (organizationName) {
      createOrganization({ variables: { name: organizationName } });
    }
  };

  const handleAddUserToOrg = () => {
    if (userId && organizationId) {
      addUserToOrganization({ variables: { userId, organizationId } });
    }
  };

  const handleAddPersonaToCollection = () => {
    if (personaId && collectionId) {
      addPersonaToCollection({ variables: { personaId, collectionId } });
    }
  };

  return (
    <>
      <Stack
        height="full"
        width="full"
        gap={0}
        bg={HelivanColor.background.primary}
        border="1px solid"
        borderColor={HelivanColor.border.primary}
        borderRadius="8px"
        spacing={0}
        overflow="hidden"
      >
        <Flex
          width="full"
          alignItems="center"
          py="16px"
          px="30px"
          height="fit-content"
          borderBottom="1px solid"
          borderBottomColor={HelivanColor.border.primary}
          position="sticky"
        >
          <HelivanText variant="sectionHeader">Internal Tooling</HelivanText>
        </Flex>
        <Stack spacing={4} px={10} py={4}>
          <HelivanText variant="largeSubheader">Clean up</HelivanText>
          <HStack spacing={4}>
            <HelivanButton variant="primary" onClick={onOpenDeleteExplorable}>
              Delete Explorable
            </HelivanButton>
            <HelivanButton variant="primary" onClick={onOpenDeletePersona}>
              Delete Persona
            </HelivanButton>
          </HStack>
          <Divider />
          <HelivanText variant="largeSubheader">Create</HelivanText>
          <HStack spacing={4}>
            <HelivanButton variant="primary" onClick={onOpenCreateOrganization}>
              Create Organization
            </HelivanButton>
            <HelivanButton variant="primary" onClick={onOpenAddUserToOrg}>
              Add user to organization
            </HelivanButton>
            <HelivanButton
              variant="primary"
              onClick={onOpenAddPersonaToCollection}
            >
              Add Persona to Collection
            </HelivanButton>
          </HStack>
        </Stack>
      </Stack>

      {/* Delete Explorable Modal */}
      <Modal isOpen={isDeleteExplorableOpen} onClose={onCloseDeleteExplorable}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Explorable</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Explorable ID</FormLabel>
              <Input
                value={explorableId}
                onChange={(e) => setExplorableId(e.target.value)}
                placeholder="Enter explorable ID"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <HelivanButton
              variant="outline"
              mr={3}
              onClick={onCloseDeleteExplorable}
            >
              Cancel
            </HelivanButton>
            <HelivanButton
              variant="primary"
              onClick={handleInitiateDeleteExplorable}
              isDisabled={!explorableId.trim()}
              style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
            >
              CONFIRM DELETE
            </HelivanButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Explorable Final Confirmation Modal */}
      <Modal
        isOpen={isExplorableFinalConfirmOpen}
        onClose={onCloseExplorableFinalConfirm}
        isCentered
        size="sm"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Final Confirmation</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HelivanText>
              Are you absolutely sure you want to delete this explorable? This
              action cannot be undone.
            </HelivanText>
            <HelivanText fontWeight="bold" mt={2}>
              ID: {explorableId}
            </HelivanText>
          </ModalBody>
          <ModalFooter>
            <HelivanButton
              variant="outline"
              mr={3}
              onClick={onCloseExplorableFinalConfirm}
            >
              Cancel
            </HelivanButton>
            <HelivanButton
              variant="primary"
              onClick={handleDeleteExplorable}
              isLoading={deleteExplorableLoading}
              style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
            >
              YES, DELETE
            </HelivanButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Persona Modal */}
      <Modal isOpen={isDeletePersonaOpen} onClose={onCloseDeletePersona}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Persona</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Persona ID</FormLabel>
              <Input
                value={personaId}
                onChange={(e) => setPersonaId(e.target.value)}
                placeholder="Enter persona ID"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <HelivanButton
              variant="outline"
              mr={3}
              onClick={onCloseDeletePersona}
            >
              Cancel
            </HelivanButton>
            <HelivanButton
              variant="primary"
              onClick={handleInitiateDeletePersona}
              isDisabled={!personaId.trim()}
              style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
            >
              CONFIRM DELETE
            </HelivanButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Persona Final Confirmation Modal */}
      <Modal
        isOpen={isPersonaFinalConfirmOpen}
        onClose={onClosePersonaFinalConfirm}
        isCentered
        size="sm"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Final Confirmation</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HelivanText>
              Are you absolutely sure you want to delete this persona? This
              action cannot be undone.
            </HelivanText>
            <HelivanText fontWeight="bold" mt={2}>
              ID: {personaId}
            </HelivanText>
          </ModalBody>
          <ModalFooter>
            <HelivanButton
              variant="outline"
              mr={3}
              onClick={onClosePersonaFinalConfirm}
            >
              Cancel
            </HelivanButton>
            <HelivanButton
              variant="primary"
              onClick={handleDeletePersona}
              isLoading={deletePersonaLoading}
              style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
            >
              YES, DELETE
            </HelivanButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create Organization Modal */}
      <Modal
        isOpen={isCreateOrganizationOpen}
        onClose={onCloseCreateOrganization}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create Organization</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Organization Name</FormLabel>
              <Input
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Enter organization name"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <HelivanButton
              variant="outline"
              mr={3}
              onClick={onCloseCreateOrganization}
            >
              Cancel
            </HelivanButton>
            <HelivanButton
              variant="primary"
              onClick={handleCreateOrganization}
              isDisabled={!organizationName.trim() || createOrgLoading}
              isLoading={createOrgLoading}
            >
              Create
            </HelivanButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add User to Organization Modal */}
      <Modal isOpen={isAddUserToOrgOpen} onClose={onCloseAddUserToOrg}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add User to Organization</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>User ID</FormLabel>
                <Input
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="Enter user ID"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Organization ID</FormLabel>
                <Input
                  value={organizationId}
                  onChange={(e) => setOrganizationId(e.target.value)}
                  placeholder="Enter organization ID"
                />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <HelivanButton
              variant="outline"
              mr={3}
              onClick={onCloseAddUserToOrg}
            >
              Cancel
            </HelivanButton>
            <HelivanButton
              variant="primary"
              onClick={handleAddUserToOrg}
              isDisabled={
                !userId.trim() || !organizationId.trim() || addUserLoading
              }
              isLoading={addUserLoading}
            >
              Create
            </HelivanButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Persona to Collection Modal */}
      <Modal
        isOpen={isAddPersonaToCollectionOpen}
        onClose={onCloseAddPersonaToCollection}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Persona to Collection</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Collection ID</FormLabel>
              <Input
                value={collectionId}
                onChange={(e) => setCollectionId(e.target.value)}
                placeholder="Enter collection ID"
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Persona ID</FormLabel>
              <Input
                value={personaId}
                onChange={(e) => setPersonaId(e.target.value)}
                placeholder="Enter persona ID"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <HelivanButton
              variant="outline"
              mr={3}
              onClick={onCloseAddPersonaToCollection}
            >
              Cancel
            </HelivanButton>
            <HelivanButton
              variant="primary"
              onClick={handleAddPersonaToCollection}
              isDisabled={
                !personaId.trim() ||
                !collectionId.trim() ||
                addPersonaToCollectionLoading
              }
              isLoading={addPersonaToCollectionLoading}
            >
              Add
            </HelivanButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
